import { useRef, useEffect, useState } from 'react'
import { v4 as uuid } from 'uuid'
import withStyles from '@material-ui/core/styles/withStyles'
import { playerStyles } from 'root/src/client/web/commonStyles'
import classNames from 'classnames'
import mixpanel from 'root/src/client/logic/analytics/mixpanel'
import { mixpanelEvents } from 'root/src/shared/constants/mixpanel'
import { PlayerError } from './PlayerError'
/**
 * This component assumes YT player script is loaded.
	*
	* Example implementation of yt script load in
	* src/client/web/list/listComponents/VideoCardListModule.js
 */
const YTPlayer = ({
	videoId, classes, autoplay, start, onChangeItemCallbackArr,
	muted, completedDare, className, onPlay, videoURL,
}) => {
	const YTClient = window.YT
	const playerRef = useRef()
	const iframeRef = useRef()
	const [hasError, setHasError] = useState(false)
	const onVideoStart = () => {
		if (onPlay) {
			onPlay()
		}
		mixpanel.time_event(mixpanelEvents.videoStop)
	}
	const onVideoPause = () => {
		const videoLength = typeof playerRef.current === 'function'
			? playerRef.current.getDuration()
			: undefined
		return mixpanel.track(mixpanelEvents.videoStop, {
			videoLength,
			videoPlayer: 'YoutubePlayer',
		})
	}
	const init = () => {
		try {
			const id = uuid()
			iframeRef.current.id = id
			playerRef.current = new YTClient.Player(id, {
				height: '100%',
				width: '100%',
				videoId,
				playerVars: {
					autoplay: autoplay ? 1 : 0,
					rel: 0,
					start: start === 0 ? 1 : start,
				},
				events: {
					onReady: () => {
						// eslint-disable-next-line no-unused-expressions
						muted === false ? playerRef.current.unMute() : playerRef.current.mute()
						iframeRef.current.style.backgroundColor = 'transparent'
						if (onChangeItemCallbackArr) {
							// eslint-disable-next-line no-param-reassign
							onChangeItemCallbackArr.current = [...onChangeItemCallbackArr.current, () => {
								if (playerRef.current.getPlayerState() === 1) {
									playerRef.current.pauseVideo()
								}
							}]
						}
					},
					onStateChange: (playerState) => {
						if (playerState.data === 1) {
							onVideoStart()
						} else if (playerState.data === 2 || playerState.data === 0) onVideoPause()
					},
					onError: () => {
						setHasError(true)
					},
				},
			})
			return id
		} catch {
			setHasError(true)
			return undefined
		}
	}

	useEffect(() => {
		if (!YTClient) {
			setHasError(true)
			return undefined
		}

		let id
		if (YTClient.loaded) {
			id = init()
		} else {
			id = YTClient.ready(init)
		}
		return () => {
			const prevPlayer = document.getElementById(id)
			if (prevPlayer) {
				prevPlayer.remove()
			}
		}
	}, [YTClient, videoId])
	useEffect(() => {
		window.addEventListener('beforeunload', onVideoPause)
		return () => {
			window.removeEventListener('beforeunload', onVideoPause)
			onVideoPause()
		}
	}, [])
	if (hasError) {
		return <PlayerError videoURL={videoURL} />
	}

	return (
		<div className={classNames(classes.iframeContainer,
			{ [classes.completedDareIframeContainer]: completedDare })}
		>
			<div ref={iframeRef} className={classNames(classes.iframe, className)} />
		</div>
	)
}

export default withStyles(playerStyles, { name: 'MuiYTPlayer' })(YTPlayer)
